// Сколько стоит месяц, за двенадцать месяцев подряд.
//
// ## Зачем
//
// Это главный вопрос генерального директора, и до 08.09.2026 продукт на него не отвечал
// вовсе: каждый отчёт показывал ровно один выбранный месяц. «Мы платим больше, чем полгода
// назад» решалось выгрузкой шести файлов Excel и сложением в уме.
//
// ## Почему столбик сложенный, а не три линии рядом
//
// Начисленное — это целое, а официальная часть, удержания и выплата — его части. Три линии
// показали бы три независимые величины и потеряли бы ровно то, что здесь важно: как менялось
// СООТНОШЕНИЕ. Сложенный столбик показывает и целое, и доли одной картинкой.
//
// Ряды обязаны складываться в начисленное — это проверяет тест сервера. Ряд равнозначных
// величин, который не сходится, человек читает не как «показано не всё», а как «программа
// посчитала неверно», и дальше не верит ни одному числу на экране.
//
// ## Что было непонятно в первом кадре
//
// Три цвета объяснялись легендой в дальнем правом углу, и связь «столбик = всё начисленное»
// нигде не называлась: человек видел двухцветную башню и не знал, что это одно число,
// поделённое на части. Теперь это написано словами прямо над графиком, а легенда стоит в той
// же строке — там, где её прочитают.
//
// Ещё в ряд попадали месяцы с нулём: ведомости за них заведены, но не посчитаны, и столбик
// выходил пустой серой плитой без единого слова. Такие месяцы в график не идут — их называет
// подпись под ним.
//
// Вид взят из набора AlignUI (`widget-budget-overview` и его `budget-overview-stack-bar-chart`).

import type { PayrollTrendOut } from '@/shared/api/endpoints/reports';
import { formatKopecksCompact, formatRubles } from '@/shared/money/format';
import { countLabel, pluralRu } from '@/shared/text/plural';
import * as Badge from '@/shared/ui/badge';
import { ChartLegend } from '@/shared/ui/chart';
import { StackBarChart } from '@/shared/ui/chart-stack-bar';

import { дельтаКПрошлому } from './дельта';

/**
 * Ряды снизу вверх. Порядок смысловой: внизу то, что человек получает на руки, наверху то,
 * что до него не доходит. При обратном порядке выплата «плавала» бы над удержаниями и её
 * нельзя было бы сравнить между месяцами — сравнивают по нижнему краю.
 */
const РЯДЫ = {
  total_payout: { label: 'К выплате', color: 'hsl(var(--information-base))' },
  deductions_total: { label: 'Аванс и удержания', color: 'hsl(var(--verified-base))' },
  official_total: { label: 'Официальная часть', color: 'hsl(var(--feature-base))' },
} as const;

/**
 * Меньше трёх столбиков — не ряд.
 *
 * На двух месяцах recharts рисует две плиты во всю ширину карточки: это не «как менялось», а
 * два числа, поставленные рядом, и на них уже отвечает ряд величин под графиком. Порог в три
 * — минимум, на котором видно направление, а не разницу между двумя точками.
 */
const МИНИМУМ_СТОЛБИКОВ = 3;

const МЕСЯЦЫ = ['месяц', 'месяца', 'месяцев'] as const;

export interface СтоимостьМесяцаProps {
  data: PayrollTrendOut;
  /**
   * Показывать ли крупное число последнего посчитанного месяца.
   *
   * Выключается в сводке за месяц: там под графиком стоит ряд величин ЗА ВЫБРАННЫЙ месяц, и
   * два крупных числа рядом — «3 261 556,10 за май» и «0,00 начислено» за сентябрь — человек
   * читает как противоречие, а не как два разных месяца.
   */
  showHeadline?: boolean;
}

export function СтоимостьМесяца({ data, showHeadline = true }: СтоимостьМесяцаProps) {
  // Месяцы, у которых ведомости заведены, но не посчитаны, в график не идут: пустой столбик
  // означал бы «месяц стоил ничего», а правда другая.
  const посчитанные = data.items.filter((месяц) => месяц.accrued > 0);
  const непосчитанных = data.items.length - посчитанные.length;
  const последний = посчитанные.at(-1);
  const предыдущий = посчитанные.at(-2);
  const метка =
    последний !== undefined && предыдущий !== undefined
      ? дельтаКПрошлому(последний.accrued, предыдущий.accrued, 'рост')
      : undefined;

  return (
    <div className='flex flex-col gap-4'>
      {showHeadline && (
        <div>
          <div className='text-paragraph-sm text-text-sub-600'>
            {последний === undefined
              ? 'Начислено по месяцам'
              : `Начислено за ${последний.label} — последний посчитанный месяц`}
          </div>
          <div className='mt-1 flex flex-wrap items-center gap-2'>
            <span className='text-title-h5 tabular-nums text-text-strong-950'>
              {formatRubles(последний?.accrued ?? 0)}
            </span>
            {метка !== undefined && (
              <Badge.Root
                variant='light'
                color={метка.tone === 'плохо' ? 'red' : метка.tone === 'хорошо' ? 'green' : 'gray'}
                size='medium'
              >
                {метка.text}
              </Badge.Root>
            )}
          </div>
        </div>
      )}

      {посчитанные.length < МИНИМУМ_СТОЛБИКОВ ? (
        <p className='text-paragraph-sm text-text-sub-600'>
          {/* Согласование — общим помощником, а не руками: до 23.09.2026 здесь стояло «Пока
              посчитан два», и ломаная фраза на экране отчёта читается как поломка программы. */}
          {посчитанные.length === 0
            ? 'Ряд по месяцам появится, когда будет посчитана хотя бы одна ведомость.'
            : `Ряд по месяцам появится, когда наберётся три посчитанных месяца. Пока ${pluralRu(посчитанные.length, ['посчитан', 'посчитаны', 'посчитано'])} ${countLabel(посчитанные.length, МЕСЯЦЫ)}.`}
        </p>
      ) : (
        <>
          {/* Главное объяснение графика, и оно стоит НАД ним. Без этой строки двухцветная
              башня — просто башня: не сказано, что её высота и есть начисленное, а цвета —
              его части. Легенда в той же строке, а не в дальнем углу карточки. */}
          <div className='flex flex-wrap items-center gap-x-4 gap-y-1'>
            <span className='text-paragraph-xs text-text-sub-600'>
              Высота столбика — всё начисленное за месяц. Оно делится на три части:
            </span>
            {Object.values(РЯДЫ).map((ряд) => (
              <ChartLegend key={ряд.label} color={ряд.color} label={ряд.label} />
            ))}
          </div>

          <StackBarChart
            data={посчитанные.map((месяц) => ({
              label: месяц.label,
              total_payout: месяц.total_payout,
              deductions_total: месяц.deductions_total,
              official_total: месяц.official_total,
            }))}
            config={РЯДЫ}
            formatTick={formatKopecksCompact}
          />

          {/* Вывод словами: то, ради чего на ряд и смотрят. Читать наклон башен глазами —
              работа, а ответ «дороже на столько-то» короче любой картинки. */}
          {последний !== undefined && предыдущий !== undefined && (
            <p className='text-paragraph-sm text-text-strong-950'>
              {последний.accrued === предыдущий.accrued
                ? `${последний.label} обошёлся ровно во столько же, сколько ${предыдущий.label}.`
                : `${последний.label} ${последний.accrued > предыдущий.accrued ? 'дороже' : 'дешевле'} ${предыдущий.label} на ${Math.abs(Math.round(((последний.accrued - предыдущий.accrued) / предыдущий.accrued) * 100))}% — это ${formatRubles(Math.abs(последний.accrued - предыдущий.accrued))}.`}
            </p>
          )}

          {непосчитанных > 0 && (
            <p className='text-paragraph-xs text-text-sub-600'>
              {/* «Ещё 5 месяца» — та же ломаная фраза, что и «посчитан два»: форма бралась одна
                  на все числа больше единицы. */}
              {непосчитанных === 1
                ? 'Ещё один месяц в графике не показан: ведомости за него заведены, но не посчитаны.'
                : `Ещё ${countLabel(непосчитанных, МЕСЯЦЫ)} в графике не показаны: ведомости за них заведены, но не посчитаны.`}
            </p>
          )}
        </>
      )}
    </div>
  );
}
